import type {
	LoaderFunction,
	ActionFunction,
	V2_MetaFunction,
} from '@remix-run/node'
import {
	type Comment as CommentType,
	type Post as PostType,
} from '~/gql/queries.ts'
import {
	useActionData,
	Form,
	useNavigation,
	useLoaderData,
} from '@remix-run/react'
import { redirect } from '@remix-run/node'
import Comment from '~/components/comment.tsx'
import Post from '~/components/post.tsx'
import { getPost, postComment } from '~/utils/graphql.ts'
import React from 'react'
import { replaceImage } from '~/utils/index.ts'

export let meta: V2_MetaFunction = ({ data }: { data: PostType }) => {
	const fullUrl = 'https://www.whiletheyweresleeping.com/' + data.slug
	return [
		{ title: `${data.title} :: While They Were Sleeping` },
		{ property: 'og:locale', content: 'en_US' },
		{ property: 'og:type', content: 'article' },
		{
			property: 'og:title',
			content: `${data.title} - While They Were Sleeping`,
		},
		{
			property: 'og:description',
			content: `${data.excerpt
				.split('<div class="sharedaddy sd-sharing-enabled">')[0]
				.replace(/(<([^>]+)>)/gi, '')} ...READ MORE`,
		},
		{ property: 'og:url', content: fullUrl },
		{ property: 'og:site_name', content: 'While They Were Sleeping' },
		{
			property: 'article:publisher',
			content: 'https://www.whiletheyweresleeping.com',
		},
		{ property: 'article:published_time', content: data.date },
		{
			property: 'og:image',
			content: data.featuredImage
				? replaceImage(data.featuredImage.node.mediaItemUrl)
				: '',
		},
		{
			property: 'og:image:width',
			content: data.featuredImage
				? data.featuredImage.node.mediaDetails.width.toString()
				: '0',
		},
		{
			property: 'og:image:height',
			content: data.featuredImage
				? data.featuredImage.node.mediaDetails.height.toString()
				: '0',
		},
		{ property: 'twitter:creator', content: '@fivekidsandabook' },
		{ property: 'twitter:card', content: 'summary_large_image' },
		{ property: 'twitter:label1', content: 'Written by' },
		{ property: 'twitter:data1', content: data.author.node.name },
		{ property: 'twitter:label2', content: 'Est. reading time' },
		{ property: 'twitter:data2', content: '4 minutes' },
		{
			property: 'twitter:image',
			content: data.featuredImage
				? replaceImage(data.featuredImage.node.mediaItemUrl)
				: '',
		},
		{
			property: 'twitter:title',
			content: `${data.title} - While They Were Sleeping`,
		},
		{
			property: 'twitter:description',
			content: `${data.excerpt
				.split('<div class="sharedaddy sd-sharing-enabled">')[0]
				.replace(/(<([^>]+)>)/gi, '')} ...READ MORE`,
		},
	]
}
//

function validateCommentName(name: string) {
	if (name.length < 3) {
		return 'The name is too short'
	}
}
function validateCommentEmail(email: string) {
	if (!email || !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
		return 'The email is invalid'
	}
}
function validateCommentContent(content: string) {
	if (content.length < 3) {
		return 'The content is too short'
	}
}

type ActionData = {
	noError?: Boolean
	formError?: string
	comment?: CommentType
	fieldErrors?: {
		content: string | undefined
		author: string | undefined
		authorEmail: string | undefined
	}
	fields?: {
		content: string
		author: string
		authorEmail: string
		commentOn: number
	}
}

export let action: ActionFunction = async ({
	request,
}): Promise<Response | ActionData> => {
	const formData = await request.formData()
	const content = formData.get('comment_content')
	const name = formData.get('comment_name')
	const email = formData.get('comment_email')
	const postId = formData.get('_post_id')

	if (
		typeof name !== 'string' ||
		typeof content !== 'string' ||
		typeof email !== 'string'
	) {
		return { formError: `Form not submitted correctly.` }
	}

	let fieldErrors = {
		content: validateCommentContent(content),
		author: validateCommentName(name),
		authorEmail: validateCommentEmail(email),
	}

	const fields = {
		content,
		author: name,
		authorEmail: email,
		commentOn: Number(postId),
	}

	if (Object.values(fieldErrors).some(Boolean)) {
		return { fieldErrors, fields }
	}

	const comment = await postComment(fields)
	if (!comment) {
		return { formError: 'Comment was not saved.  Please try again.' }
	}

	return { comment }
}

export let loader: LoaderFunction = async ({ params }): Promise<PostType> => {
	try {
		const post = await getPost(params.slug || '')
		return post
	} catch (error) {
		throw redirect('/')
	}
}

export default function PostRoute() {
	const transition = useNavigation()
	const post = useLoaderData<PostType>()
	const actionData = useActionData<ActionData>()

	const commentFormRef = React.useRef<HTMLFormElement>(null)

	React.useEffect(() => {
		if (actionData?.comment) {
			commentFormRef.current?.reset()
			if (!post?.comments?.nodes) {
				post.comments = { nodes: [] }
			}
			post.comments.nodes = [...post.comments?.nodes, actionData.comment]
		}
	}, [actionData?.comment, post])

	return (
		<>
			<Post post={post} />
			<div className="mb-4 w-full">
				{post.comments?.nodes.map(
					comment =>
						comment.approved && <Comment key={comment.id} comment={comment} />,
				)}
			</div>
			<Form ref={commentFormRef} method="post">
				<h3 className="mt-6 border-b-2 border-theme-gray pb-2 text-center text-xs font-thin uppercase tracking-widest text-gray-800 ">
					Post a Comment
				</h3>
				<div className="mt-4">
					<input type="hidden" name="_post_id" value={post.databaseId} />
					<div className="mb-2 flex flex-col">
						<label
							htmlFor="comment_content"
							className="text-sm uppercase tracking-widest text-gray-700"
						>
							Comment
						</label>
						<textarea
							name="comment_content"
							defaultValue={actionData?.fields?.content}
							id="comment_content"
							className="w-full rounded border border-theme-gray p-2 text-sm"
							aria-invalid={
								Boolean(actionData?.fieldErrors?.content) || undefined
							}
							aria-describedby={
								actionData?.fieldErrors?.content ? 'content-error' : undefined
							}
						/>
						{actionData?.fieldErrors?.content ? (
							<p
								className="form-validation-error"
								role="alert"
								id="content-error"
							>
								{actionData.fieldErrors.content}
							</p>
						) : null}
					</div>
					<div className="mb-2 flex flex-col">
						<label
							htmlFor="comment_name"
							className="text-sm uppercase tracking-widest text-gray-700"
						>
							Name
						</label>
						<input
							type="text"
							defaultValue={actionData?.fields?.author}
							name="comment_name"
							id="comment_name"
							className="w-full rounded border border-theme-gray p-2 text-sm"
							aria-invalid={
								Boolean(actionData?.fieldErrors?.author) || undefined
							}
							aria-describedby={
								actionData?.fieldErrors?.author ? 'name-error' : undefined
							}
						/>
						{actionData?.fieldErrors?.author ? (
							<p className="form-validation-error" role="alert" id="name-error">
								{actionData.fieldErrors.author}
							</p>
						) : null}
					</div>
					<div className="mb-2 flex flex-col">
						<label
							htmlFor="comment_email"
							className="text-sm uppercase tracking-widest text-gray-700"
						>
							Email
						</label>
						<input
							type="email"
							defaultValue={actionData?.fields?.authorEmail}
							name="comment_email"
							id="comment_email"
							className="w-full rounded border border-theme-gray p-2 text-sm"
							aria-invalid={
								Boolean(actionData?.fieldErrors?.authorEmail) || undefined
							}
							aria-describedby={
								actionData?.fieldErrors?.authorEmail ? 'email-error' : undefined
							}
						/>
						{actionData?.fieldErrors?.authorEmail ? (
							<p
								className="form-validation-error"
								role="alert"
								id="email-error"
							>
								{actionData.fieldErrors.authorEmail}
							</p>
						) : null}
					</div>
					<div className="mt-4">
						<button
							type="submit"
							disabled={Boolean(transition.state === 'submitting')}
							className={`more mx-auto border-2 border-theme-yellow px-4 py-2 text-sm font-light uppercase tracking-widest text-theme-yellow hover:text-theme-brown ${
								false && 'cursor-not-allowed opacity-30 hover:text-theme-yellow'
							}`}
						>
							{transition.state === 'submitting'
								? 'Posting Comment...'
								: `Post Comment`}
						</button>
					</div>
				</div>
			</Form>
		</>
	)
}
